import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularLoader from "../loader/CircularLoader";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import FixedSizeImageCropper from "../FixedSizeImageCropper/FixedSizeImageCropper";
import CameraAltIcon from "@mui/icons-material/CameraAltOutlined";
import PersonIcon from "@mui/icons-material/PersonOutline";
import { checkAndConvertToHTML } from "../../utils/Constant";
import {
  complete_profile_update_api,
  get_all_sectors_by_client_type,
  reset_enhanced_password_api,
} from "../../DAL/TalentSourceAPI/TalentSourceApi";
import { getPasswordStrength } from "../menu/PasswirdStrength";
import InfoIcon from "@mui/icons-material/Info";
import GeneralCkeditor from "../editors/GeneralCkEditor";
import { enqueueSnackbar } from "notistack";
import { useIds } from "../../hook/AdminContext";
import { s3baseUri } from "../../config/config";
import GeneralMuiModal from "../menu/MenuModalMui";
import {
  get_types_list,
  update_company_profile,
} from "../../DAL/features/features";
import ClientProfileFields from "./components/ClientProfileFields";
import { get_client_details } from "../../DAL/clientTypes/ClientTpes";

export default function ProfileDialog({
  openModal,
  userInfo,
  setOpenModal,
  isCompanyProfile = false,
  feature_slug = "",
  companyType = "",
  callBackFunction = () => {},
}) {
  const INITIAL_VALUES = {
    first_name: "",
    email: "",
    anchorEl: null,
    avatar: "",
    dialogOpen: false,
    loading: false,
    last_name: "",
    post_code: "",
    contact_number: "",
    status: true,
    company_name: "",
    contact_name: "",
    twitter_link: "",
    youtube: "",
    linkedIn_link: "",
    facebook_link: "",
    instagram_link: "",
    location: "",
    website_url: "",
    street_address: "",
    state: "",
    city: "",
    country: "",
    about: "",
    image: "",
    password: "",
    confirm_password: "",
    title: "",
    lastName: "",
    short_summary: "",
    profile_type_id: "",
    tagline: "",
  };

  const [inputs, setInputs] = useState(INITIAL_VALUES);
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState({});

  const { fetctUserDetail } = useIds();

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);

  const [typesList, setTypesList] = useState([]);

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);

  const fileChangedHandler = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Handle SVG files differently
    if (file.type === "image/svg+xml") {
      const reader = new FileReader();
      reader.onload = () => {
        // Directly set the SVG as preview and file
        setCroppedImage(reader.result);
        setInputs((prev) => ({ ...prev, image: file }));
      };
      reader.readAsDataURL(file);
      return; // Skip the cropping modal for SVG
    }

    // Original handling for other image types
    const reader = new FileReader();
    reader.onload = () => {
      setFile(reader.result);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleUpdateCompanyProfile = async () => {
    if (inputs.profile_type_id.length == 0) {
      enqueueSnackbar("Type is required.", { variant: "error" });
      return;
    }
    setInputs((prev) => ({ ...prev, loading: true }));

    const postData = {
      short_summary: inputs.short_summary,
      profile_type_id: inputs.profile_type_id,
      user_type: companyType,
    };
    const resp = await update_company_profile(userInfo?.profile_id, postData);
    if (resp.code == 200) {
      await fetctUserDetail();
      if (typeof callBackFunction === "function") {
        callBackFunction();
      }
      enqueueSnackbar(resp.message, { variant: "success" });
      setOpenModal(false);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setInputs((prev) => ({ ...prev, loading: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (inputs.tagline?.length > 120) {
      enqueueSnackbar("Tagline must be 120 characters or less.", {
        variant: "error",
      });
      return;
    }

    if (userInfo?.is_temporary_password === true) {
      if (!inputs.password) {
        enqueueSnackbar("New password is required", { variant: "error" });
        return;
      }

      const passwordErrors = [];
      if (inputs.password.length < 8)
        passwordErrors.push("at least 8 characters");
      if (!/[A-Z]/.test(inputs.password))
        passwordErrors.push("one uppercase letter");
      if (!/[a-z]/.test(inputs.password))
        passwordErrors.push("one lowercase letter");
      if (!/[0-9]/.test(inputs.password)) passwordErrors.push("one number");
      if (!/[^A-Za-z0-9]/.test(inputs.password))
        passwordErrors.push("one special character");

      if (passwordErrors.length > 0) {
        enqueueSnackbar(`Password must contain: ${passwordErrors.join(", ")}`, {
          variant: "error",
        });
        return;
      }

      if (inputs.password !== inputs.confirm_password) {
        enqueueSnackbar("New Password and confirm New Password don't match", {
          variant: "error",
        });
        return;
      }
    }

    // URL validation function
    const validateUrl = (url) => {
      if (url && !/^https?:\/\//i.test(url)) {
        return false; // Invalid URL
      }
      return true; // Valid URL
    };

    // Validate social media URLs if provided (optional fields) - Always run this validation
    const socialMediaFields = [
      { name: "facebook_link", label: "Facebook URL" },
      { name: "linkedIn_link", label: "LinkedIn URL" },
      { name: "instagram_link", label: "Instagram URL" },
      { name: "youtube", label: "YouTube URL" },
      { name: "twitter_link", label: "Twitter URL" },
    ];

    for (const field of socialMediaFields) {
      const value = inputs[field.name]?.trim();

      // Only validate if user has provided a value
      if (value && !validateUrl(value)) {
        enqueueSnackbar(`${field.label} must start with http:// or https://`, {
          variant: "error",
        });
        return;
      }
    }

    if (userInfo?.is_profile_completed === false) {
      const hasNewImage = inputs?.image && typeof inputs.image !== "string";
      const hasOldImage = croppedImage && typeof croppedImage === "string";
      const hasExistingImage =
        userInfo?.profile_image?.thumbnail_1 || userInfo?.profile_image;

      if (!hasNewImage && !hasOldImage && !hasExistingImage) {
        enqueueSnackbar("Profile image is required", { variant: "error" });
        return;
      }

      const requiredFields = [
        { name: "company_name", label: "Company name" },
        { name: "contact_name", label: "Contact name" },
        { name: "contact_number", label: "Contact number" },
        { name: "post_code", label: "Post code" },
        { name: "location", label: "Location" },
        { name: "email", label: "Email" },
        { name: "website_url", label: "Website" },
        { name: "about", label: "About Yourself" },
      ];

      for (const field of requiredFields) {
        const value = inputs[field.name]?.trim();

        // Required check
        if (!value) {
          enqueueSnackbar(`${field.label} is required`, { variant: "error" });
          return;
        }

        if (["website_url"].includes(field.name)) {
          if (!validateUrl(value)) {
            enqueueSnackbar(
              `${field.label} must start with http:// or https://`,
              { variant: "error" }
            );
            return;
          }
        }
      }

      // Check if any client type requires sector selection
      if (inputs?.clientTypeHavingSectors?.length > 0) {
        const hasSelectedSectors = inputs?.clientTypeHavingSectors.every(
          (clientType) => {
            return clientType.sector && clientType.sector.slug;
          }
        );

        if (!hasSelectedSectors) {
          enqueueSnackbar(
            "Sector selection is required for all applicable client types",
            { variant: "error" }
          );
          return;
        }
      }
    }

    setInputs((prev) => ({ ...prev, loading: true }));
    const is_image_exists = inputs.image;
    const is_image_uploaded = croppedImage && typeof croppedImage === "string";
    const hasExistingImage =
      userInfo?.profile_image?.thumbnail_1 || userInfo?.profile_image;

    // Debug the image states

    // Only require image if user is completing profile for the first time
    if (
      userInfo?.is_profile_completed === false &&
      !is_image_exists &&
      !is_image_uploaded
    ) {
      enqueueSnackbar("Profile image is required", { variant: "error" });
      setInputs((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      const formData = new FormData();
      if (userInfo?.is_temporary_password === true) {
        formData.append("password", inputs.password);
        formData.append("confirm_password", inputs.confirm_password);
      }

      formData.append("company_name", inputs.company_name);
      formData.append("location", inputs.location);
      formData.append("email", inputs.email);
      formData.append("contact_number", inputs.contact_number);
      formData.append("post_code", inputs.post_code);
      formData.append("linkedin", inputs.linkedIn_link);
      formData.append("youtube", inputs.youtube);
      formData.append("twitter", inputs.twitter_link);
      formData.append("instagram", inputs.instagram_link);
      formData.append("facebook", inputs.facebook_link);
      formData.append("contact_name", inputs.contact_name);
      formData.append("website", inputs.website_url);
      formData.append("about", inputs.about);
      formData.append("tagline", inputs.tagline);
      formData.append("client_type", getClientType());
      // Debug logging

      if (is_image_uploaded) {
        console.log("is_image_uploaded");
        // Use croppedImage only if no existing image from userInfo
        const response = await fetch(croppedImage);
        const blob = await response.blob();
        const urlParts = croppedImage.split("/");
        const filename = "profile_image.jpg";
        const file = new File([blob], filename, { type: blob.type });
        formData.append("image", file);
      } else {
        console.log(
          "No image found - this should not happen if validation passed"
        );
      }

      const response = await complete_profile_update_api(formData);

      if (response.code === 200) {
        const data = { ...response.data };

        setInputs((old) => ({ ...old, ...data }));
        const updatedUser = {
          ...response.data,
          is_temporary_password: false,
          is_profile_completed: true,
        };
        await fetctUserDetail();
        setOpenModal(false);
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(response.message, { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("An error occurred while updating", { variant: "error" });
    } finally {
      setInputs((prev) => ({ ...prev, loading: false }));
    }
  };
  const handleSubmitCandidate = async () => {
    if (userInfo?.is_temporary_password === true) {
      if (!inputs.password) {
        enqueueSnackbar("New password is required", { variant: "error" });
        return;
      }

      const passwordErrors = [];
      if (inputs.password.length < 8)
        passwordErrors.push("at least 8 characters");
      if (!/[A-Z]/.test(inputs.password))
        passwordErrors.push("one uppercase letter");
      if (!/[a-z]/.test(inputs.password))
        passwordErrors.push("one lowercase letter");
      if (!/[0-9]/.test(inputs.password)) passwordErrors.push("one number");
      if (!/[^A-Za-z0-9]/.test(inputs.password))
        passwordErrors.push("one special character");

      if (passwordErrors.length > 0) {
        enqueueSnackbar(`Password must contain: ${passwordErrors.join(", ")}`, {
          variant: "error",
        });
        return;
      }

      if (inputs.password !== inputs.confirm_password) {
        enqueueSnackbar("New Password and confirm New Password don't match", {
          variant: "error",
        });
        return;
      }
    }
    setInputs((prev) => ({ ...prev, loading: true }));
    const data = {
      password: inputs.password,
      confirm_password: inputs.confirm_password,
    };
    try {
      const response = await reset_enhanced_password_api(data);
      if (response.code === 200) {
        await fetctUserDetail();
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
        setOpenModal(false);
      } else {
        enqueueSnackbar(response.message, { variant: "error" });
        // setUserInfo((prev) => ({ ...prev, is_temporary_password: false }));
      }
    } catch (error) {
      enqueueSnackbar("An error occurred while submitting candidate", {
        variant: "error",
      });
    } finally {
      setInputs((prev) => ({ ...prev, loading: false }));
    }
  };

  const getClientType = () => {
    if (!Array.isArray(inputs?.client_type)) return "[]";

    const mapped = inputs.client_type.map((obj) => {
      // find matching client type in clientTypeHavingSectors by slug
      const match = inputs.clientTypeHavingSectors?.find(
        (ct) => ct.slug === obj.slug
      );

      return {
        slug: obj.slug,
        sector_slug: match?.sector?.slug || obj?.sector?.slug || "",
      };
    });

    return JSON.stringify(mapped);
  };

  const getClientTypeHavingSectors = (sectorObj, userObj) => {
    // Step 1: filter client types that have sector arrays in sectorObj
    let typeArrayWithSector = (userObj?.client_type || []).filter(
      (obj) => sectorObj[obj.slug]?.length > 0
    );

    // Step 2: enrich sector only if slug is missing
    return typeArrayWithSector.map((clientType) => {
      const hasSlug = clientType.sector?.slug;

      if (!hasSlug) {
        const matchedSector = sectorObj[clientType.slug]?.find(
          (s) => s._id === clientType.sector?.sector_id
        );

        if (matchedSector) {
          return {
            ...clientType,
            sector: {
              ...clientType.sector,
              slug: matchedSector.slug,
            },
          };
        } else {
          return {
            ...clientType,
            sector: {
              ...clientType.sector,
              slug: "",
            },
          };
        }
      }

      return clientType; // unchanged if slug already exists or no match
    });
  };

  const fetchSectors = async (userObj) => {
    const clientTypes = userObj?.client_type || [];
    if (clientTypes.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Helper to normalize slug
      const getSlug = (clientTypeObj) => {
        if (typeof clientTypeObj === "string") return clientTypeObj;
        if (clientTypeObj && typeof clientTypeObj === "object") {
          return (
            clientTypeObj.slug ||
            clientTypeObj.title?.toLowerCase().replace(/\s+/g, "_")
          );
        }
        return null;
      };

      // Prepare API calls
      const requests = clientTypes
        .map((obj) => getSlug(obj))
        .filter(Boolean) // remove nulls
        .map(async (slug) => {
          try {
            const response = await get_all_sectors_by_client_type(
              "",
              50,
              0,
              slug
            );

            if (response.code === 200) {
              return { slug, sectors: response?.data?.sectors || [] };
            } else {
              enqueueSnackbar(`Failed to fetch sectors for ${slug}`, {
                variant: "error",
              });
              return { slug, sectors: [] };
            }
          } catch (err) {
            console.error(`Error fetching ${slug}:`, err);
            enqueueSnackbar(`Error fetching sectors for ${slug}`, {
              variant: "error",
            });
            return { slug, sectors: [] };
          }
        });

      // Run all in parallel
      const results = await Promise.all(requests);

      // Build object
      const sectorsData = results.reduce((acc, { slug, sectors }) => {
        acc[slug] = sectors;
        return acc;
      }, {});

      setInputs((prev) => ({
        ...prev,
        clientTypeHavingSectors: getClientTypeHavingSectors(
          sectorsData,
          userObj
        ),
      }));

      setSectors(sectorsData);
    } catch (error) {
      enqueueSnackbar("Error fetching sectors", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSetInputs = (obj) => {
    setInputs((old) => ({
      ...old,
      ...obj,
      password: "",
      instagram_link: obj.social_links?.instagram,
      facebook_link: obj.social_links?.facebook,
      twitter_link: obj.social_links?.twitter,
      linkedIn_link: obj.social_links?.linkedin,
      youtube: obj.social_links?.youtube,
      contact_name: obj?.contact_name,
      street_address: obj?.street || obj?.address?.street,
      post_code: obj?.address?.post_code || obj?.post_code,
      about: obj?.about ? checkAndConvertToHTML(obj?.about) : "",
      image: obj?.profile_image?.thumbnail_1 || "",
      email: obj?.email,
      title: obj?.company_name,
      lastName: obj?.location,
      location: obj?.location,
      company_name: obj?.company_name,
      contact_number: obj?.contact_number,
      website_url: obj?.website_url,
      profile_type_id: Array.isArray(obj?.[companyType]?.profile_type)
        ? obj[companyType].profile_type.map((item) => item._id) // case 1: if profile type is array
        : obj?.[companyType]?.profile_type?._id
        ? [obj[companyType].profile_type._id] // case 2: if profile type is a single object
        : [], // case 3: nothing
      short_summary: companyType
        ? obj?.[companyType]?.short_summary || ""
        : obj?.short_summary || "",
    }));

    if (obj?.client_type) {
      fetchSectors(obj);
    } else {
      setLoading(false);
    }
  };

  const handleGetClientDetails = async () => {
    const resp = await get_client_details(userInfo.profile_id);
    if (resp.code == 200) {
      handleSetInputs(resp.user);
    } else {
      setLoading(false);
      enqueueSnackbar(resp.message, { variant: "error" });
    }
  };

  useEffect(() => {
    if (openModal) {
      if (userInfo?.isCompanyProfile) {
        handleGetClientDetails();
      } else {
        handleSetInputs(userInfo);
      }
    }
  }, [userInfo, openModal]);

  const handleMenuOpen = (event) => {
    setInputs((old) => ({ ...old, anchorEl: event.currentTarget }));
  };
  const handleMenuClose = () => {
    setInputs((old) => ({ ...old, anchorEl: null }));
  };

  const handleViewProfile = () => {
    setInputs((old) => ({ ...old, dialogOpen: true }));
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setInputs((old) => ({ ...old, dialogOpen: false }));
  };

  const handleUpdateImage = () => {
    fileInputRef.current.click();
    handleMenuClose();
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((old) => ({ ...old, [name]: value }));
  };

  const handleSectorChange = (slug, newSectorId) => {
    setInputs((prev) => ({
      ...prev,
      clientTypeHavingSectors: prev.clientTypeHavingSectors.map((clientType) =>
        clientType.slug === slug
          ? {
              ...clientType,
              sector: {
                ...clientType.sector,
                slug: newSectorId,
              },
            }
          : clientType
      ),
    }));
  };

  return (
    <>
      {cropModalOpen && (
        <>
          <FixedSizeImageCropper
            image={file}
            maxSize={{ width: 1080, height: 1080 }}
            onClose={() => setCropModalOpen(false)}
            aspectRatio={1 / 1}
            handleCropComplete={(file) => {
              setCroppedImage(URL.createObjectURL(file));
              setInputs((prev) => ({ ...prev, image: file }));
              setCropModalOpen(false);
            }}
            roundCropAreaPixels={true}
            open={cropModalOpen}
          />
        </>
      )}

      {inputs.dialogOpen && (
        <GeneralMuiModal
          open={inputs.dialogOpen}
          setOpen={handleDialogClose}
          componentToPass={
            <>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <img
                  className="de-flex text-center justify-cotent-center"
                  alt="Profile"
                  src={
                    croppedImage ||
                    (inputs.image && typeof inputs.image === "string"
                      ? s3baseUri + inputs.image
                      : "")
                  }
                  style={{ width: 200, height: 200, margin: "0 auto" }}
                />
              </div>
            </>
          }
        />
      )}
      <Dialog
        open={openModal}
        onClose={() => {}}
        PaperProps={{
          style: {
            borderRadius: "8px",
            width: "95%",
            maxWidth: "1000px",
            margin: "16px",
            animation: "slideIn 0.3s ease-out",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
          },
        }}
        disableEscapeKeyDown
      >
        <DialogTitle
          style={{
            backgroundColor: "var(--portal-primary-color)",
            color: "white",
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: 600, color: "#fff", fontSize: "inherit" }}>
            {isCompanyProfile
              ? "Company Profile"
              : userInfo?.role === "candidate"
              ? "Security Alert"
              : "Complete Your Profile"}
          </span>
        </DialogTitle>

        {loading ? (
          <CircularLoader />
        ) : (
          <>
            <DialogContent style={{ paddingTop: "24px" }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  isCompanyProfile
                    ? handleUpdateCompanyProfile()
                    : userInfo?.role === "candidate"
                    ? handleSubmitCandidate(e)
                    : handleSubmit(e);
                }}
                id="profileForm"
              >
                {!isCompanyProfile && (
                  <Box display="flex" alignItems="flex-start" mb={3}>
                    <Typography variant="body1">
                      <Box display="flex" alignItems="flex-start">
                        <WarningAmberIcon
                          style={{
                            color: "var(--portal-secondary-color)",
                            fontSize: "24px",
                            marginRight: "12px",
                            marginTop: "2px",
                          }}
                        />
                        {userInfo?.is_temporary_password &&
                        userInfo?.is_profile_completed === false ? (
                          <Box>
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 500, marginBottom: "8px" }}
                            >
                              Important Security Notice
                            </Typography>
                            <Typography variant="body1" paragraph>
                              {
                                "Your account is currently using a temporary password. To ensure your account security, please complete your profile information and set a new password."
                              }
                            </Typography>
                          </Box>
                        ) : userInfo?.is_temporary_password ? (
                          <Box>
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 500, marginBottom: "8px" }}
                            >
                              Important Security Notice
                            </Typography>
                            <Typography variant="body1" paragraph>
                              {
                                "For your security, your account is currently using a temporary password. To safeguard your personal information, we strongly recommend that you reset your password now by setting a secure and permanent one."
                              }
                            </Typography>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center">
                            <Typography variant="body1" paragraph>
                              Please review and complete your profile
                              information to continue.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Typography>
                  </Box>
                )}

                {isCompanyProfile &&
                  inputs?.[companyType] &&
                  inputs?.[companyType]?.publish == false && (
                    <>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" paragraph>
                          Please review and complete your profile information to
                          continue.
                        </Typography>
                      </Box>
                    </>
                  )}
                {/* Show only when temporary password exists */}
                {userInfo?.is_temporary_password === true && (
                  <>
                    {userInfo.role === "candidate" && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          style={{ marginBottom: "16px" }}
                        >
                          Reset Password
                        </Typography>
                      </Grid>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Grid container spacing={2}>
                        {/* New Password Field with Strength Indicator */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            name="password"
                            autoCapitalize="off"
                            autoComplete="new-password"
                            label="New Password"
                            type={showNewPassword ? "text" : "password"}
                            value={inputs.password}
                            onChange={handleChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() =>
                                      setShowNewPassword(!showNewPassword)
                                    }
                                    edge="end"
                                  >
                                    {showNewPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />

                          {inputs.password && (
                            <Box sx={{ mt: 1 }}>
                              <div style={{ width: "100%", marginBottom: 3 }}>
                                <div
                                  style={{
                                    height: 8,
                                    borderRadius: 4,
                                    background: "#eee",
                                    width: "100%",
                                    overflow: "hidden",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${
                                        getPasswordStrength(inputs.password)
                                          .percentage
                                      }%`,
                                      background: getPasswordStrength(
                                        inputs.password
                                      ).color,
                                      transition: "width 0.3s, background 0.3s",
                                    }}
                                  ></div>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: 14,
                                      color: getPasswordStrength(
                                        inputs.password
                                      ).color,
                                      textTransform: "capitalize",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {getPasswordStrength(inputs.password).level}
                                    <span
                                      style={{
                                        marginLeft: 6,
                                        cursor: "pointer",
                                        verticalAlign: "middle",
                                        color: "#1976d2",
                                        display: "inline-flex",
                                        alignItems: "center",
                                      }}
                                      onClick={() =>
                                        setShowRequirements((prev) => !prev)
                                      }
                                      title="Show password requirements"
                                    >
                                      <InfoIcon
                                        style={{
                                          fontSize: 16,
                                          color: "#1976d2",
                                        }}
                                      />
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </Box>
                          )}

                          {/* Password Requirements */}
                          {showRequirements && (
                            <div
                              style={{
                                background: "#f8f9fa",
                                borderRadius: 6,
                                padding: "12px 16px",
                                marginBottom: 8,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              }}
                            >
                              <strong style={{ fontSize: 14 }}>
                                Password Requirements
                              </strong>
                              <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                                <li
                                  style={{
                                    color:
                                      inputs.password.length >= 8
                                        ? "green"
                                        : "red",
                                  }}
                                >
                                  At least 8 characters
                                </li>
                                <li
                                  style={{
                                    color: /[A-Z]/.test(inputs.password)
                                      ? "green"
                                      : "red",
                                  }}
                                >
                                  An uppercase letter (A-Z)
                                </li>
                                <li
                                  style={{
                                    color: /[a-z]/.test(inputs.password)
                                      ? "green"
                                      : "red",
                                  }}
                                >
                                  A lowercase letter (a-z)
                                </li>
                                <li
                                  style={{
                                    color: /\d/.test(inputs.password)
                                      ? "green"
                                      : "red",
                                  }}
                                >
                                  A number (0-9)
                                </li>
                                <li
                                  style={{
                                    color:
                                      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
                                        inputs.password
                                      )
                                        ? "green"
                                        : "red",
                                  }}
                                >
                                  A special character (!@#$%^&* etc.)
                                </li>
                              </ul>
                            </div>
                          )}
                        </Grid>

                        {/* Confirm Password Field - Standalone without strength indicator */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            name="confirm_password"
                            autoCapitalize="off"
                            autoComplete="new-password"
                            label="Confirm New Password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={inputs.confirm_password}
                            onChange={handleChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() =>
                                      setShowConfirmPassword(
                                        !showConfirmPassword
                                      )
                                    }
                                    edge="end"
                                  >
                                    {showConfirmPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}

                {/* Show profile fields only when profile is incomplete and user is client */}
                {((userInfo?.is_profile_completed === false &&
                  userInfo?.role === "client") ||
                  isCompanyProfile) && (
                  <>
                    {/* Profile Form */}
                    <Grid item xs={12} mt={1}>
                      <Typography variant="h6" style={{ marginBottom: "16px" }}>
                        Update Profile Information
                      </Typography>
                    </Grid>

                    <ClientProfileFields
                      inputs={inputs}
                      croppedImage={croppedImage}
                      setCroppedImage={setCroppedImage}
                      setInputs={setInputs}
                      isCompanyProfile={isCompanyProfile}
                      isAllowSectorFlow={true}
                      handleSectorChange={handleSectorChange}
                      sectors={sectors}
                      companyType={companyType}
                      isTypeAndShortSummaryAllow={isCompanyProfile}
                      feature_slug={feature_slug}
                    />
                  </>
                )}
              </form>
            </DialogContent>
            <DialogActions
              style={{
                padding: "16px 24px",
                margin: 0,
              }}
            >
              {isCompanyProfile && (
                <button
                  className="btn-outlined"
                  onClick={() => {
                    setOpenModal(false);
                  }}
                >
                  Close
                </button>
              )}

              <button
                className="btn-contained"
                type="submit"
                form="profileForm"
                disabled={inputs.loading}
              >
                {inputs.loading
                  ? userInfo?.role === "candidate" && !userInfo.isCompanyProfile
                    ? "Resetting..."
                    : "Updating..."
                  : userInfo?.role === "candidate" && !userInfo.isCompanyProfile
                  ? "Reset Password"
                  : "Update Profile"}
              </button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
